'use client'

/**
 * FilerBook — one big filer's latest 13F equity book (holdings, ranked by value) for /smart-money/[filer].
 * New and increased positions are flagged, each holding links to its stock hub, and a live day-move shows
 * how each is trading now. Search + a "new only" toggle.
 */

import { useEffect, useMemo, useState } from 'react'
import Link from 'next/link'
import type { SmartFilerBook } from '@/lib/dashboard/smartMoney'
import TickerLogo from '@/components/TickerLogo'

const bn = (n: number) => {
  if (!n || n <= 0) return '—'
  if (n >= 1e12) return `$${(n / 1e12).toFixed(2)}T`
  if (n >= 1e9) return `$${(n / 1e9).toFixed(2)}B`
  if (n >= 1e6) return `$${(n / 1e6).toFixed(0)}M`
  return `$${Math.round(n / 1e3)}K`
}
const CHANGE: Record<string, { label: string; cls: string } | null> = {
  new: { label: 'New', cls: 'bg-sky-500/15 text-sky-300' },
  increased: { label: 'Added', cls: 'bg-violet-500/15 text-violet-300' },
  reduced: { label: 'Trimmed', cls: 'bg-zinc-700/50 text-zinc-400' },
  held: null,
}
function titleCase(s: string) {
  const SMALL = new Set(['and', 'of', 'the', 'for', 'to', 'in'])
  return s.toLowerCase().replace(/[a-z0-9]+/g, (w, i: number) => (i > 0 && SMALL.has(w) ? w : w[0].toUpperCase() + w.slice(1)))
}

export default function FilerBook({ book }: { book: SmartFilerBook }) {
  const [q, setQ] = useState('')
  const [newOnly, setNewOnly] = useState(false)
  const [moves, setMoves] = useState<Record<string, number>>({})

  useEffect(() => {
    const syms = book.holdings.map((h) => h.symbol).filter(Boolean).slice(0, 60) as string[]
    if (!syms.length) return
    let alive = true
    const pull = () =>
      fetch(`/api/watchlist/quotes?symbols=${encodeURIComponent(syms.join(','))}`, { cache: 'no-store' })
        .then((r) => (r.ok ? r.json() : null))
        .then((j) => { if (alive && Array.isArray(j?.quotes)) { const m: Record<string, number> = {}; for (const t of j.quotes) if (t?.symbol && t.chgPct != null) m[String(t.symbol).toUpperCase()] = Number(t.chgPct); setMoves(m) } })
        .catch(() => {})
    pull(); const id = setInterval(() => { if (!document.hidden) pull() }, 30000)
    return () => { alive = false; clearInterval(id) }
  }, [book.holdings])

  const rows = useMemo(() => {
    const term = q.trim().toLowerCase()
    return book.holdings.filter((h) => {
      if (newOnly && h.change !== 'new' && h.change !== 'increased') return false
      if (!term) return true
      return h.name.toLowerCase().includes(term) || (h.symbol ?? '').toLowerCase().includes(term)
    })
  }, [book.holdings, q, newOnly])

  return (
    <div>
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <button onClick={() => setNewOnly((v) => !v)} className={`rounded-lg border px-2.5 py-1 text-[12px] font-semibold transition-colors ${newOnly ? 'border-amber-500 bg-amber-500 text-black' : 'border-zinc-800 bg-zinc-900/60 text-zinc-400 hover:text-zinc-200'}`}>
          New &amp; increased{book.newCount ? ` · ${book.newCount}` : ''}
        </button>
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Filter holdings"
          className="ml-auto w-full rounded-lg border border-zinc-800 bg-zinc-900/60 px-3 py-1.5 text-sm text-zinc-200 outline-none placeholder:text-zinc-600 focus:border-zinc-600 sm:w-56" />
      </div>

      <ol className="overflow-hidden rounded-2xl border border-zinc-800/70 bg-zinc-900/30">
        {rows.map((h, i) => {
          const ch = CHANGE[h.change]
          const wPct = book.totalValue > 0 ? (h.value / book.totalValue) * 100 : 0
          const mv = h.symbol ? moves[h.symbol] : undefined
          return (
            <li key={`${h.symbol ?? h.name}-${i}`} className={`grid grid-cols-[auto_1fr_auto] items-center gap-3 px-3 py-2 transition-colors hover:bg-zinc-800/40 ${i > 0 ? 'border-t border-zinc-800/60' : ''}`}>
              <span className="flex items-center gap-2.5">
                <span className="w-5 text-right text-[11px] font-bold tabular-nums text-zinc-600">{i + 1}</span>
                {h.symbol ? (
                  <TickerLogo symbol={h.symbol} size={32} rounded="rounded-lg" />
                ) : <span className="h-8 w-8 shrink-0 rounded-lg bg-zinc-800" />}
              </span>
              <span className="min-w-0">
                <span className="flex items-center gap-2">
                  {h.symbol
                    ? <Link href={`/stocks/${encodeURIComponent(h.symbol)}`} className="text-[14px] font-extrabold tracking-tight text-white hover:text-amber-300">${h.symbol}</Link>
                    : <span className="text-[13px] font-bold text-zinc-300">{titleCase(h.name)}</span>}
                  {ch && <span className={`rounded px-1.5 py-0.5 text-[9px] font-bold uppercase ${ch.cls}`}>{ch.label}{h.change === 'increased' && h.changePct != null ? ` +${Math.round(h.changePct)}%` : ''}</span>}
                  {mv != null && <span className={`text-[11px] font-bold tabular-nums ${mv >= 0 ? 'text-emerald-400' : 'text-rose-400'}`}>{mv >= 0 ? '+' : ''}{mv.toFixed(1)}%</span>}
                </span>
                <span className="block truncate text-[11px] text-zinc-500">{titleCase(h.name)}</span>
              </span>
              <span className="flex shrink-0 flex-col items-end">
                <span className="text-[13px] font-bold tabular-nums text-zinc-100">{bn(h.value)}</span>
                <span className="text-[10px] tabular-nums text-zinc-600">{wPct.toFixed(1)}% of book</span>
              </span>
            </li>
          )
        })}
        {rows.length === 0 && <li className="px-4 py-8 text-center text-sm text-zinc-500">No holdings match.</li>}
      </ol>
    </div>
  )
}
